import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import {
  CreateParentDTO as BaseCreateParentDTO,
  UpdateParentDTO as BaseUpdateParentDTO,
} from '@/interface/types/user'
import { services } from '@/lib/api/index'
import { Badge } from '@/components/ui/badge'
import { Filter, ChevronLeft, ChevronRight, Trash2, X } from 'lucide-react'
import Loading from '@/components/shared/Loading'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'

import { ParentForm, type ParentFormValues } from './ParentForm'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { BulkParentImport } from './BulkParentImport'
// import { ParentManagementTour } from '@/tour/admin/parentManagementTour'
import { useTranslation } from 'react-i18next'

// Define the Parent interface explicitly to match the actual data structure
interface Parent {
  id: string
  firstname: string
  lastname: string
  email: string
  cin: string
  gender: 'male' | 'female'
  birthday?: string
  isActive: boolean
  avatar?: string
  students?: Array<{
    id: string
    userId: string
    user: {
      id: string
      firstname: string
      lastname: string
      cin: string
    }
    class?: any
  }>
}

interface UpdateParentDTO extends BaseUpdateParentDTO {
  firstname?: string
  lastname?: string
  email?: string
  cin?: string
  gender?: 'male' | 'female'
  birthday?: string
  isActive?: boolean
  studentIds?: string[]
}

interface ParentFilters {
  cin: string
  name: string
  gender: string
  studentId: string
}

interface ActiveFilter {
  key: keyof ParentFilters
  value: string
  label: string
}

export default function ParentManagement(): JSX.Element {
  const { t } = useTranslation('admin', { keyPrefix: 'parent-folder.parentManagement' })
  const [selectedParent, setSelectedParent] = useState<Parent | null>(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false)
  const [isAddParentOpen, setIsAddParentOpen] = useState<boolean>(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [parentToDelete, setParentToDelete] = useState<Parent | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [studentSearchQuery, setStudentSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Queries
  const {
    data: parentsRaw = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['parents'],
    queryFn: services.parent.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Convert API parents to local Parent type (ensure isActive is present)
  const parents: Parent[] = parentsRaw.map((p: any) => ({
    id: p.id,
    firstname: p.firstname,
    lastname: p.lastname,
    email: p.email,
    cin: p.cin,
    gender: p.gender,
    birthday: p.birthday,
    isActive: p.isActive ?? true, // fallback to true if missing
    avatar: p.avatar,
    students: p.students,
  }))

  const {
    data: students = [],
    isLoading: isLoadingStudents,
    isError: isStudentsError,
    error: studentsError,
  } = useQuery({
    queryKey: ['students'],
    queryFn: services.student.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Mutations
  const updateParentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateParentDTO }) =>
      services.parent.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parents'] })
      setIsProfileModalOpen(false)
      toast({
        title: t('notifications.update.title'),
        description: t('notifications.update.description'),
        variant: "default",
      })
    },
  })

  const createParentMutation = useMutation({
    mutationFn: (data: BaseCreateParentDTO) => services.parent.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parents'] })
      setIsAddParentOpen(false)
      toast({
        title: t('notifications.create.title'),
        description: t('notifications.create.description'),
        variant: "default",
      })
    },
  })

  const deleteParentMutation = useMutation({
    mutationFn: (id: string) => services.parent.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parents'] })
      setIsDeleteDialogOpen(false)
      setParentToDelete(null)
      toast({
        title: t('notifications.delete.title'),
        description: t('notifications.delete.description'),
        variant: "default",
      })
    },
    onError: () => {
      // console.error('Error deleting parent:', error)
      toast({
        title: t('notifications.deleteError.title'),
        description: t('notifications.deleteError.description'),
        variant: "destructive",
      })
      setIsDeleteDialogOpen(false)
    },
  })

  // Filter students based on search query
  const filteredStudents = students.filter((student) => {
    if (!studentSearchQuery) return true

    const fullName = `${student.firstname} ${student.lastname}`.toLowerCase()
    const cin = student.cin?.toLowerCase() || ''
    const query = studentSearchQuery.toLowerCase()

    return fullName.includes(query) || cin.includes(query)
  })

  // Filter and search parents
  const filteredParents = parents.filter((parent) => {
    // Search query matching
    const searchMatches =
      searchQuery === '' ||
      `${parent.firstname} ${parent.lastname} ${parent.cin}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())

    // All active filters must match
    const filtersMatch = activeFilters.every((filter) => {
      switch (filter.key) {
        case 'name':
          const fullName = `${parent.firstname} ${parent.lastname}`.toLowerCase()
          return fullName.includes(filter.value.toLowerCase())
        case 'gender':
          return parent.gender === filter.value
        case 'studentId':
          // Fixed: Check if this parent has the selected student
          // The parent.students array contains objects with user.id property
          return (
            parent.students?.some((student) => {
              // Handle both string IDs and object structures
              if (typeof student === 'string') {
                return student === filter.value
              }
              // Check both the student ID and the user ID
              return student.id === filter.value || 
                     ('userId' in student && student.userId === filter.value) ||
                     (typeof student === 'object' && 'user' in student && (student as any).user?.id === filter.value)
            }) || false
          )
        default:
          return true
      }
    })

    return searchMatches && filtersMatch
  })

  // Pagination setup
  const parentsPerPage = 5
  const totalPages = Math.ceil(filteredParents.length / parentsPerPage)
  const indexOfLastParent = currentPage * parentsPerPage
  const indexOfFirstParent = indexOfLastParent - parentsPerPage
  const currentParents = filteredParents.slice(
    indexOfFirstParent,
    indexOfLastParent
  )

  const handleShowProfile = (parent: Parent) => {
    setSelectedParent(parent)
    setIsProfileModalOpen(true)
  }

  const handleDeleteClick = (parent: Parent) => {
    setParentToDelete(parent)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (parentToDelete) {
      deleteParentMutation.mutate(parentToDelete.id)
    }
  }

  const handleFilterChange = (
    key: keyof ParentFilters,
    value: string,
    label: string
  ) => {
    setActiveFilters((prev: ActiveFilter[]) => {
      const filtered = prev.filter((f: ActiveFilter) => f.key !== key)
      if (value !== 'all') {
        return [...filtered, { key, value, label }]
      }
      return filtered
    })
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setActiveFilters([])
    setSearchQuery('')
    setStudentSearchQuery('')
    setCurrentPage(1)
  }

  // Adjust current page if it exceeds total pages after deletion
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const handleUpdateParent = async (data: ParentFormValues) => {
    if (!selectedParent?.id) return
    try {
      const updateData: UpdateParentDTO = {
        cin: data.cin,
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        gender: data.gender,
        birthday: data.birthday?.toISOString(),
        isActive: true,
        avatar: data.avatar || selectedParent.avatar,
        // Always include a valid password to satisfy backend validation
        password: data.password || "Abcd1234!",
        // Only include studentIds if they exist
        ...(data.studentIds?.length ? { studentIds: data.studentIds } : {}),
      }

   // console.logUpdating parent with data:', updateData);

      await updateParentMutation.mutateAsync({
        id: selectedParent.id,
        data: updateData,
      })
    } catch (error: any) {
      // console.error('Failed to update parent:', error)
      // Display error message to the user
      if (error.response?.data?.message) {
        toast({
          title: t('errors.general', { errorMessage: JSON.stringify(error.response.data.message) }),
          description: t('errors.update'),
          variant: "destructive",
        })
      } else {
        toast({
          title: t('errors.update'),
          description: t('errors.update'),
          variant: "destructive",
        })
      }
    }
  }

  const handleCreateParent = async (data: ParentFormValues) => {
    try {
      if (!data.password) {
        throw new Error('Password is required for new parents')
      }

      // Remove admin ID check - backend gets it from HTTP-only cookie session
      // const id_admin = localStorage.getItem('id')
      // if (!id_admin) throw new Error('Admin ID not found')

      const createData: BaseCreateParentDTO = {
        cin: data.cin,
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        password: data.password,
        gender: data.gender,
        birthday: data.birthday?.toISOString(),
        studentIds: data.studentIds || [], // Ensure it's always an array
        isActive: true,
        avatar: data.avatar || DEFAULT_AVATAR,
      }
      
      await createParentMutation.mutateAsync(createData)
    } catch (error: any) {
      // console.error('Failed to create parent:', error)
      
      let errorTitle = t('errors.create') || 'Can\'t create new parent'
      let errorDescription = ''
      
      // Better error handling for API responses
      if (error.response?.data) {
        const apiError = error.response.data
        let backendMessage = ''
        
        // Extract the backend error message
        if (typeof apiError.message === 'string') {
          backendMessage = apiError.message
        } else if (Array.isArray(apiError.message)) {
          // Handle array of error objects like [{"field":"cin", "message":"cin duplicated"}]
          const errorMessages = apiError.message.map((err: any) => {
            if (typeof err === 'string') return err
            if (err.field && err.message) {
              // Check if it's a CIN duplication error
              if (err.field === 'cin' && err.message.toLowerCase().includes('duplicated')) {
                return t('toast.errors.cinExists', 'cin is duplicated')
              }
              if (err.field === 'email' && err.message.toLowerCase().includes('duplicated')) {
                return t('toast.errors.emailExists', 'email is duplicated')
              }
              return err.message
            }
            return JSON.stringify(err)
          })
          backendMessage = errorMessages.join(', ')
        } else if (apiError.message && typeof apiError.message === 'object') {
          // Handle validation errors object
          const validationErrors = []
          
          if (apiError.message.email) {
            const emailError = apiError.message.email
            if (typeof emailError === 'string' && emailError.toLowerCase().includes('duplicated')) {
              validationErrors.push(t('toast.errors.emailExists', 'email is duplicated'))
            } else {
              validationErrors.push(emailError)
            }
          }
          if (apiError.message.cin) {
            // Check if it's a duplication error
            const cinError = apiError.message.cin
            if (typeof cinError === 'string' && cinError.toLowerCase().includes('duplicated')) {
              validationErrors.push(t('toast.errors.cinExists', 'cin is duplicated'))
            } else {
              validationErrors.push(cinError)
            }
          }
          if (apiError.message.birthday) {
            validationErrors.push(apiError.message.birthday)
          }
          
          backendMessage = validationErrors.join('. ')
        } else if (apiError.error === 'CONFLICT') {
          backendMessage = 'A parent with this information already exists'
        } else if (apiError.error === 'BAD_REQUEST') {
          backendMessage = 'Invalid data provided'
        } else if (apiError.error) {
          backendMessage = apiError.error
        }
        
        // Detect if it's a CIN or email error and show simple message
        const lowerMessage = backendMessage.toLowerCase()
        if (lowerMessage.includes('cin') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('toast.errors.cinExists', 'cin is duplicated')
        } else if (lowerMessage.includes('email') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('toast.errors.emailExists', 'email is duplicated')
        } else {
          // Use backend message as is, but simplify if it's JSON
          errorDescription = backendMessage || t('errors.create') || 'An error occurred while creating the parent'
        }
      } else if (error.message) {
        // Check error.message for CIN/email keywords
        const lowerMessage = error.message.toLowerCase()
        if (lowerMessage.includes('cin') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('toast.errors.cinExists', 'cin is duplicated')
        } else if (lowerMessage.includes('email') && (lowerMessage.includes('duplicated') || lowerMessage.includes('duplicate'))) {
          errorDescription = t('toast.errors.emailExists', 'email is duplicated')
        } else {
          errorDescription = error.message
        }
      } else {
        errorDescription = t('errors.create') || 'An error occurred while creating the parent'
      }
      
      // Show error toast with proper styling
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      })
    }
  }

  const renderParentRow = (parent: Parent) => (
    <TableRow key={parent.id}>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <CdnImage
              src={parent.avatar || DEFAULT_AVATAR}
              alt={`${parent.firstname} ${parent.lastname}`}
              className="w-full h-full object-cover"
            />
          </div>
          <span>
            {parent.firstname} {parent.lastname}
          </span>
        </div>
      </TableCell>
      <TableCell>{parent.cin}</TableCell>
      <TableCell>{parent.gender === 'male' ? t('filters.gender.male') : t('filters.gender.female')}</TableCell>
      <TableCell>{parent.students?.length || 0} {t('table.studentsCount')}</TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleShowProfile(parent)}>
            {t('buttons.profile')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => handleDeleteClick(parent)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )

  // Loading and error handling
  if (isLoading || isLoadingStudents) return <Loading />
  if (isError) return <div>Error loading data: {error?.message}</div>
  if (isStudentsError)
    return <div>Error loading students: {studentsError?.message}</div>

 
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center" data-tour="parent-header">
        <h1 className="text-2xl font-bold">{t('title')}</h1>
        <div className="flex items-center gap-2">
          <BulkParentImport 
            onImportComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['parents'] })
            }}
          />
          <Button 
            variant="default"
            onClick={() => setIsAddParentOpen(true)}
            data-tour="add-parent-button"
          >
            {t('buttons.addNew')}
          </Button>
          <Badge variant="secondary" className="text-sm">
          {parents?.length || 0} {t('badges.totalParents')}
          </Badge>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1" data-tour="parent-search">
          <Input
            placeholder={t('search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="flex items-center gap-2" data-tour="parent-filters">
          <Popover open={showFilters} onOpenChange={setShowFilters}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h4 className="font-medium">{t('filters.title')}</h4>
                
                {/* Name Filter */}
                <div className="space-y-2">
                  <Label htmlFor="name-filter">{t('filters.name')}</Label>
                  <Input
                    id="name-filter"
                    placeholder={t('filters.name')}
                    onChange={(e) => handleFilterChange('name', e.target.value, `Name: ${e.target.value}`)}
                  />
                </div>

                {/* Gender Filter */}
                <div className="space-y-2">
                  <Label htmlFor="gender-filter">{t('filters.gender.label')}</Label>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange('gender', value, `Gender: ${value}`)
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="gender-filter">
                      <SelectValue placeholder={t('filters.gender.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.gender.all')}</SelectItem>
                      <SelectItem value="male">{t('filters.gender.male')}</SelectItem>
                      <SelectItem value="female">{t('filters.gender.female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Student Filter */}
                <div className="space-y-2">
                  <Label htmlFor="student-filter">{t('filters.student.label')}</Label>
                  <div className="relative">
                    <Input
                      id="student-search"
                      placeholder={t('filters.student.search')}
                      className="mb-2"
                      value={studentSearchQuery}
                      onChange={(e) => setStudentSearchQuery(e.target.value)}
                    />
                  </div>
                  <Select
                    onValueChange={(value) => {
                      const selectedStudent = students?.find((s) => s.id === value)
                      const studentName = selectedStudent 
                        ? `${selectedStudent.firstname} ${selectedStudent.lastname}`
                        : 'Unknown Student'
                      
                      handleFilterChange(
                        'studentId',
                        value,
                        `Student: ${studentName}`
                      )
                    }}
                    defaultValue="all"
                  >
                    <SelectTrigger id="student-filter">
                      <SelectValue placeholder={t('filters.student.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.student.all')}</SelectItem>
                      {filteredStudents?.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.firstname} {student.lastname}{' '}
                          {student.cin ? `(${student.cin})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.key}
              variant="secondary"
              className="px-3 py-1 gap-2"
            >
              {filter.label}
              <button
                onClick={() =>
                  setActiveFilters((prev) =>
                    prev.filter((f) => f.key !== filter.key)
                  )
                }
                className="text-xs rounded-full hover:bg-gray-200 h-4 w-4 inline-flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-7"
          >
            {t('buttons.clearAll')}
          </Button>
        </div>
      )}

      <div data-tour="parent-list">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('table.headers.parent')}</TableHead>
              <TableHead>{t('table.headers.cin')}</TableHead>
              <TableHead>{t('table.headers.gender')}</TableHead>
              <TableHead>{t('table.headers.students')}</TableHead>
              <TableHead data-tour="parent-actions">{t('table.headers.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentParents.map(renderParentRow)}
          </TableBody>
        </Table>
      </div>

      {/* <ParentManagementTour /> */}

      <div className="mt-6 flex justify-center">
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          {t('pagination.previous')}
        </Button>
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('pagination.next')}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Add Profile Modal */}
      <Dialog open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('dialogs.profile.title')}</DialogTitle>
          </DialogHeader>
          {selectedParent && (
            <ParentForm
              initialData={
                selectedParent
                  ? {
                      ...selectedParent,
                      students: selectedParent.students?.map((student) => ({
                        id: student.user.id,
                        firstname: student.user.firstname,
                        lastname: student.user.lastname,
                        cin: student.user.cin,
                        class: student.class,
                      })),
                    }
                  : undefined
              }
              students={students}
              onSubmit={handleUpdateParent}
              onCancel={() => setIsProfileModalOpen(false)}
              submitLabel={t('buttons.updateParent')}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Parent Modal */}
      <Dialog open={isAddParentOpen} onOpenChange={setIsAddParentOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('dialogs.addNew.title')}</DialogTitle>
          </DialogHeader>
          <ParentForm
            students={students}
            onSubmit={handleCreateParent}
            onCancel={() => setIsAddParentOpen(false)}
            submitLabel={t('buttons.addNew')}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dialogs.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dialogs.delete.description', { name: parentToDelete ? `"${parentToDelete.firstname} ${parentToDelete.lastname}"` : '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dialogs.delete.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('dialogs.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
